import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { api } from "../api";
import { useAuth } from "../auth";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import MasterDropdown from "./MasterDropdown";

const STATUSES = ["Open", "Closed", "To Start", "Offer Declined", "On Hold", "Closed-Resigned", "Cancelled"];
const GENDERS = ["Male", "Female"];
const PRIORITIES = ["High", "Medium", "Low"];
const OFFER_STAGES = ["Negotiation", "Offer to be Released", "Offer Released", "In Process"];

const empty = {
  candidate_name: "", designation: "", designation_offer: "",
  brand: "", product_brand: "", status: "Open",
  country: "", city: "", region: "", department: "",
  recruiter: "", hiring_manager: "",
  gender: "", age: "", experience: "", qualification: "",
  budget_type: "", approximate_budget: "",
  priority: "Medium", replacement_of: "",
  employment_type: "", trf_date: "", trf_hold_date: "", trf_reopen_date: "",
  offer_date: "", offer_stage: "", interview_status: "", source_channel: "",
  date_of_joining: "", notice_period: "",
  unit: "", business_unit: "", category: "",
  referrer_name: "", referrer_amount: "", rehiring_employee_name: "",
};

export default function PositionFormDialog({ open, onOpenChange, position, onSaved }) {
  const { user } = useAuth();
  const canSeeBudget = user?.role === "Admin" || user?.role === "Management";
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (position) {
      const cleaned = { ...empty, ...position };
      ["trf_date", "offer_date", "date_of_joining"].forEach((k) => {
        if (cleaned[k]) cleaned[k] = String(cleaned[k]).slice(0, 10);
      });
      setData(cleaned);
    } else setData(empty);
  }, [position, open]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Required master-driven fields
    const required = { brand: "Entity", city: "City", region: "Region", employment_type: "Employment Type" };
    for (const [k, label] of Object.entries(required)) {
      if (!data[k] || !String(data[k]).trim()) {
        toast.error(`Please select a ${label}`);
        return;
      }
    }
    setLoading(true);
    try {
      const payload = { ...data };
      ["gender", "trf_date", "trf_hold_date", "trf_reopen_date", "offer_date", "date_of_joining", "offer_stage", "unit", "business_unit", "category"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      // Age & Experience are free-text — strip if empty
      ["age", "experience"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
          delete payload[k];
        }
      });
      if (payload.referrer_amount === "" || payload.referrer_amount === null || payload.referrer_amount === undefined) {
        delete payload.referrer_amount;
      } else {
        payload.referrer_amount = Number(payload.referrer_amount);
      }
      if (position) {
        await api.put(`/positions/${position.id}`, payload);
        toast.success("Position updated");
      } else {
        await api.post("/positions", payload);
        toast.success("Position created");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{position ? "Edit Position" : "Add Position"}</DialogTitle>
          <DialogDescription>Fill in candidate, role, and tracking details.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5" data-testid="position-form">
          <Section title="Role">
            <Field label="Designation *">
              <Input required value={data.designation} onChange={(e) => set("designation", e.target.value)} data-testid="pf-designation" />
            </Field>
            <Field label="Designation Offer">
              <Input
                value={data.designation_offer}
                onChange={(e) => set("designation_offer", e.target.value)}
                placeholder="Final designation in offer letter"
                data-testid="pf-designation-offer"
              />
            </Field>
            <Field label="Entity *">
              <MasterDropdown
                kind="entities"
                value={data.brand}
                onChange={(v) => set("brand", v)}
                testId="pf-brand"
              />
            </Field>
            <Field label="Brand">
              <MasterDropdown
                kind="brands"
                value={data.product_brand}
                onChange={(v) => set("product_brand", v)}
                testId="pf-product-brand"
                allowEmpty
              />
            </Field>
            <Field label="Department *">
              <MasterDropdown
                kind="departments"
                value={data.department}
                onChange={(v) => set("department", v)}
                testId="pf-department"
              />
            </Field>
            <Field label="Country">
              <MasterDropdown
                kind="countries"
                value={data.country}
                onChange={(v) => set("country", v)}
                testId="pf-country"
                allowEmpty
              />
            </Field>
            <Field label="City *">
              <MasterDropdown
                kind="cities"
                value={data.city}
                onChange={(v) => set("city", v)}
                testId="pf-city"
              />
            </Field>
            <Field label="Region *">
              <MasterDropdown
                kind="regions"
                value={data.region}
                onChange={(v) => set("region", v)}
                testId="pf-region"
              />
            </Field>
            <Field label="Unit">
              <MasterDropdown
                kind="units"
                value={data.unit}
                onChange={(v) => set("unit", v)}
                testId="pf-unit"
                allowEmpty
              />
            </Field>
            <Field label="Business Unit">
              <MasterDropdown
                kind="business_units"
                value={data.business_unit}
                onChange={(v) => set("business_unit", v)}
                testId="pf-business-unit"
                allowEmpty
              />
            </Field>
            <Field label="Category">
              <MasterDropdown
                kind="categories"
                value={data.category}
                onChange={(v) => set("category", v)}
                testId="pf-category"
                allowEmpty
              />
            </Field>
          </Section>

          <Section title="Candidate">
            <Field label="Candidate Name">
              <Input value={data.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} />
            </Field>
            <Field label="Gender">
              <Select value={data.gender || "none"} onValueChange={(v) => set("gender", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Age">
              <Input
                value={data.age}
                onChange={(e) => set("age", e.target.value)}
                placeholder="e.g. 32, 28-35, N/A"
                data-testid="pf-age"
              />
            </Field>
            <Field label="Experience">
              <Input
                value={data.experience}
                onChange={(e) => set("experience", e.target.value)}
                placeholder="e.g. 5, 10-16, 10+"
                data-testid="pf-experience"
              />
            </Field>
            <Field label="Qualification">
              <Input value={data.qualification} onChange={(e) => set("qualification", e.target.value)} />
            </Field>
            <Field label="Notice Period">
              <Input value={data.notice_period} onChange={(e) => set("notice_period", e.target.value)} placeholder="e.g. 60 days" />
            </Field>
            <Field label="Source">
              <MasterDropdown
                kind="sources"
                value={data.source_channel}
                onChange={(v) => set("source_channel", v)}
                testId="pf-source"
                allowEmpty
              />
            </Field>
          </Section>

          <Section title="Hiring">
            <Field label="Recruiter *">
              <MasterDropdown
                kind="recruiters"
                value={data.recruiter}
                onChange={(v) => set("recruiter", v)}
                testId="pf-recruiter"
              />
            </Field>
            <Field label="Hiring Manager *">
              <Input value={data.hiring_manager} onChange={(e) => set("hiring_manager", e.target.value)} data-testid="pf-hm" placeholder="Name or email" />
            </Field>
            <Field label="Status">
              <Select value={data.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="pf-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select value={data.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Budget Type">
              <MasterDropdown
                kind="budget_types"
                value={data.budget_type}
                onChange={(v) => set("budget_type", v)}
                testId="pf-budget-type"
                allowEmpty
              />
            </Field>
            {canSeeBudget && (
              <Field label="Approximate Budget">
                <Input
                  value={data.approximate_budget}
                  onChange={(e) => set("approximate_budget", e.target.value)}
                  placeholder="e.g. ₹12-15 LPA"
                  data-testid="pf-approximate-budget"
                />
              </Field>
            )}
            <Field label="Employment Type">
              <MasterDropdown
                kind="employment_types"
                value={data.employment_type}
                onChange={(v) => set("employment_type", v)}
                testId="pf-employment-type"
              />
            </Field>
            {data.budget_type === "Replacement" && (
              <Field label="Replacement Of" full>
                <Input value={data.replacement_of} onChange={(e) => set("replacement_of", e.target.value)} placeholder="Name of person being replaced" />
              </Field>
            )}
            {data.budget_type === "Internal Transfer" && (
              <Field label="Rehiring Name of Employee" full>
                <Input
                  value={data.rehiring_employee_name}
                  onChange={(e) => set("rehiring_employee_name", e.target.value)}
                  placeholder="Name of employee being rehired / transferred"
                  data-testid="pf-rehiring-employee"
                />
              </Field>
            )}
            <Field label="Referrer Name">
              <Input
                value={data.referrer_name}
                onChange={(e) => set("referrer_name", e.target.value)}
                placeholder="If referred, employee's name"
                data-testid="pf-referrer-name"
              />
            </Field>
            <Field label="Referral Amount">
              <Input
                type="number"
                min={0}
                step="0.01"
                value={data.referrer_amount}
                onChange={(e) => set("referrer_amount", e.target.value)}
                placeholder="0"
                data-testid="pf-referrer-amount"
              />
            </Field>
          </Section>

          <Section title="Pipeline">
            <Field label="TRF Date">
              <Input type="date" value={data.trf_date || ""} onChange={(e) => set("trf_date", e.target.value)} />
            </Field>
            <Field label="TRF Hold Date">
              <Input type="date" value={data.trf_hold_date || ""} onChange={(e) => set("trf_hold_date", e.target.value)} data-testid="pf-trf-hold" />
            </Field>
            <Field label="TRF Reopen Date">
              <Input type="date" value={data.trf_reopen_date || ""} onChange={(e) => set("trf_reopen_date", e.target.value)} data-testid="pf-trf-reopen" />
            </Field>
            <Field label="Offer Date">
              <Input type="date" value={data.offer_date || ""} onChange={(e) => set("offer_date", e.target.value)} />
            </Field>
            <Field label="Date of Joining">
              <Input type="date" value={data.date_of_joining || ""} onChange={(e) => set("date_of_joining", e.target.value)} />
            </Field>
            <Field label="Offer Stage">
              <Select value={data.offer_stage || "none"} onValueChange={(v) => set("offer_stage", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="pf-offer-stage"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {OFFER_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Interview Status">
              <Input value={data.interview_status} onChange={(e) => set("interview_status", e.target.value)} placeholder="e.g. Round 2 cleared" />
            </Field>
          </Section>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="pf-submit">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {position ? "Save changes" : "Create position"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3 font-semibold">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2 lg:col-span-3" : ""}>
      <Label className="text-xs text-muted-foreground mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
